import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../context/TierContext';
import { FeatureGate, UpgradePrompt } from '../components/FeatureGate';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { DatePicker } from '../components/ui/date-picker';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area } from 'recharts';
import { Download, FileText, Truck, Calendar, BarChart3, DollarSign, TrendingUp, AlertTriangle, Users, Clock, Crown, Activity, ShieldCheck, Wrench, UserCheck } from 'lucide-react';
import { format, subDays } from 'date-fns';

const COLORS = ['#ffd108', '#FF4F00', '#3B82F6', '#8B5CF6', '#EC4899', '#10B981', '#F59E0B'];

const Reports = () => {
  const { api } = useAuth();
  const { hasFeature, currentTier } = useTier();
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [activeTab, setActiveTab] = useState('standard');
  const [tenantSettings, setTenantSettings] = useState({ date_format: 'DD/MM/YYYY' });
  
  // Format date according to tenant settings
  const formatDateDisplay = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const dateFormat = tenantSettings.date_format || 'DD/MM/YYYY';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthShort = monthNames[date.getMonth()];
    
    switch (dateFormat) {
      case 'DD/MM/YYYY':
        return `${day}/${month}/${year}`;
      case 'MM/DD/YYYY':
        return `${month}/${day}/${year}`;
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`;
      case 'DD-MMM-YYYY':
        return `${day}-${monthShort}-${year}`;
      default:
        return `${day}/${month}/${year}`;
    }
  };
  
  // Standard Reports State
  const [jobsReport, setJobsReport] = useState(null);
  const [utilizationReport, setUtilizationReport] = useState(null);
  const [revenueReport, setRevenueReport] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Premium Reports State
  const [premiumUtilization, setPremiumUtilization] = useState(null);
  const [downtimeAnalysis, setDowntimeAnalysis] = useState(null);
  const [jobProfitability, setJobProfitability] = useState(null);
  const [complianceSummary, setComplianceSummary] = useState(null);
  const [customerLTV, setCustomerLTV] = useState(null);
  const [subcontractorPerformance, setSubcontractorPerformance] = useState(null);
  const [premiumLoading, setPremiumLoading] = useState(false);
  
  // Fetch tenant settings on mount
  useEffect(() => {
    const fetchTenantSettings = async () => {
      try {
        const res = await api.get('/settings/tenant');
        if (res.data) {
          setTenantSettings({ date_format: res.data.date_format || 'DD/MM/YYYY' });
        }
      } catch (error) {
        console.error('Failed to fetch tenant settings');
      }
    };
    fetchTenantSettings();
  }, [api]);

  const fetchStandardReports = async () => {
    if (!hasFeature('standard_reports') && !hasFeature('reports')) return;
    setLoading(true);
    try {
      const [jobsRes, utilRes, revenueRes] = await Promise.all([
        api.get('/reports/jobs', { params: { start_date: startDate, end_date: endDate } }),
        api.get('/reports/equipment-utilization', { params: { start_date: startDate, end_date: endDate } }),
        api.get('/reports/revenue', { params: { start_date: startDate, end_date: endDate } })
      ]);
      setJobsReport(jobsRes.data);
      setUtilizationReport(utilRes.data);
      setRevenueReport(revenueRes.data);
    } catch (error) {
      toast.error('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchPremiumReports = async () => {
    if (!hasFeature('premium_reports')) return;
    setPremiumLoading(true);
    try {
      const [utilRes, downtimeRes, profitRes, complianceRes, ltvRes, subRes] = await Promise.all([
        api.get('/reports/premium/equipment-utilization', { params: { start_date: startDate, end_date: endDate } }),
        api.get('/reports/premium/downtime-analysis', { params: { start_date: startDate, end_date: endDate } }),
        api.get('/reports/premium/job-profitability', { params: { start_date: startDate, end_date: endDate } }),
        api.get('/reports/premium/compliance-summary'),
        api.get('/reports/premium/customer-lifetime-value'),
        api.get('/reports/premium/subcontractor-performance', { params: { start_date: startDate, end_date: endDate } })
      ]);
      setPremiumUtilization(utilRes.data);
      setDowntimeAnalysis(downtimeRes.data);
      setJobProfitability(profitRes.data);
      setComplianceSummary(complianceRes.data);
      setCustomerLTV(ltvRes.data);
      setSubcontractorPerformance(subRes.data);
    } catch (error) {
      toast.error('Failed to fetch premium reports');
    } finally {
      setPremiumLoading(false);
    }
  };

  useEffect(() => {
    if (hasFeature('standard_reports') || hasFeature('reports')) {
      fetchStandardReports();
    }
  }, [api, hasFeature]);

  const handleGenerateReports = () => {
    if (activeTab === 'standard') {
      fetchStandardReports();
    } else {
      fetchPremiumReports();
    }
  };

  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    toast.success('Report exported');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', { 
      style: 'currency', 
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Prepare chart data
  const jobStatusData = jobsReport ? [
    { name: 'Draft', value: jobsReport.by_status.draft, color: '#6B7280' },
    { name: 'Confirmed', value: jobsReport.by_status.confirmed, color: '#3B82F6' },
    { name: 'Completed', value: jobsReport.by_status.completed, color: '#ffd108' },
    { name: 'Cancelled', value: jobsReport.by_status.cancelled, color: '#EF4444' }
  ].filter(d => d.value > 0) : [];

  const utilizationData = utilizationReport?.equipment?.map(eq => ({
    name: eq.asset_id.length > 15 ? eq.asset_id.substring(0, 15) + '...' : eq.asset_id,
    days: eq.total_allocation_days,
    jobs: eq.allocation_count
  })) || [];

  const customerRevenueData = revenueReport?.by_customer?.slice(0, 8).map((item, index) => ({
    name: item.customer_name.length > 20 ? item.customer_name.substring(0, 20) + '...' : item.customer_name,
    revenue: item.revenue,
    dockets: item.docket_count,
    fill: COLORS[index % COLORS.length]
  })) || [];

  const equipmentRevenueData = revenueReport?.by_equipment_type?.map((item, index) => ({
    name: item.equipment_type,
    revenue: item.revenue,
    dockets: item.docket_count,
    fill: COLORS[index % COLORS.length]
  })) || [];

  const monthlyRevenueData = revenueReport?.by_month?.map(item => ({
    month: item.month,
    revenue: item.revenue,
    dockets: item.docket_count
  })) || [];

  // Premium chart data
  const utilizationRateData = premiumUtilization?.equipment?.slice(0, 10).map((eq, index) => ({
    name: eq.asset_id.length > 12 ? eq.asset_id.substring(0, 12) + '...' : eq.asset_id,
    rate: eq.utilization_rate,
    fill: eq.utilization_rate >= 70 ? '#10B981' : eq.utilization_rate >= 30 ? '#F59E0B' : '#EF4444'
  })) || [];

  const downtimeStatusData = downtimeAnalysis ? [
    { name: 'Available (No bookings)', value: downtimeAnalysis.by_status.available.idle_days, color: '#6B7280' },
    { name: 'Maintenance', value: downtimeAnalysis.by_status.maintenance.idle_days, color: '#F59E0B' },
    { name: 'Sold', value: downtimeAnalysis.by_status.sold.idle_days, color: '#EF4444' }
  ].filter(d => d.value > 0) : [];

  const complianceTrendData = complianceSummary?.monthly_trend?.map(item => ({
    month: item.month,
    pre_start: item.pre_start,
    incident: item.incident
  })) || [];

  const hasPremiumAccess = hasFeature('premium_reports');
  const hasStandardAccess = hasFeature('standard_reports') || hasFeature('reports');

  return (
    <FeatureGate 
      feature="reports"
      title="Reports & Analytics"
      description="Get detailed insights into your jobs, equipment utilization, and revenue with comprehensive reports and data exports."
    >
      <div className="space-y-6" data-testid="reports-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold">Reports</h1>
            <p className="text-muted-foreground">Analytics and data exports</p>
          </div>
          {hasPremiumAccess && (
            <Badge className="bg-purple-500 text-white">
              <Crown className="h-3 w-3 mr-1" />
              Premium Reports
            </Badge>
          )}
        </div>

        {/* Date Range Filter */}
        <Card className="card-industrial">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row items-end gap-4">
              <div className="space-y-2 flex-1">
                <Label className="label-overline">Start Date</Label>
                <DatePicker
                  value={startDate}
                  onChange={setStartDate}
                  formatDisplay={formatDateDisplay}
                  className="w-full"
                  testId="report-start-date"
                />
              </div>
              <div className="space-y-2 flex-1">
                <Label className="label-overline">End Date</Label>
                <DatePicker
                  value={endDate}
                  onChange={setEndDate}
                  formatDisplay={formatDateDisplay}
                  minDate={startDate}
                  className="w-full"
                  testId="report-end-date"
                />
              </div>
              <Button 
                onClick={handleGenerateReports} 
                disabled={loading || premiumLoading} 
                className="btn-volt" 
                data-testid="generate-report-btn"
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                {loading || premiumLoading ? 'Loading...' : 'Generate'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Report Type Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="standard" data-testid="standard-reports-tab">
              <BarChart3 className="h-4 w-4 mr-2" />
              Standard Reports
            </TabsTrigger>
            <TabsTrigger value="premium" data-testid="premium-reports-tab">
              <Crown className="h-4 w-4 mr-2" />
              Premium Reports
            </TabsTrigger>
          </TabsList>

          {/* ==================== STANDARD REPORTS ==================== */}
          <TabsContent value="standard" className="space-y-6">
            <Tabs defaultValue="revenue">
              <TabsList>
                <TabsTrigger value="revenue" data-testid="revenue-report-tab">
                  <DollarSign className="h-4 w-4 mr-2" />
                  Revenue Summary
                </TabsTrigger>
                <TabsTrigger value="jobs" data-testid="jobs-report-tab">
                  <FileText className="h-4 w-4 mr-2" />
                  Jobs Report
                </TabsTrigger>
                <TabsTrigger value="utilization" data-testid="utilization-report-tab">
                  <Truck className="h-4 w-4 mr-2" />
                  Equipment Utilization
                </TabsTrigger>
              </TabsList>

              {/* Revenue Tab */}
              <TabsContent value="revenue" className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="card-industrial">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-[#ffd108]/20">
                          <DollarSign className="h-5 w-5 text-[#ffd108]" />
                        </div>
                        <div>
                          <p className="label-overline">Total Revenue</p>
                          <p className="text-2xl font-heading font-bold text-[#ffd108]">
                            {formatCurrency(revenueReport?.summary?.total_revenue || 0)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="card-industrial">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-500/20">
                          <FileText className="h-5 w-5 text-blue-500" />
                        </div>
                        <div>
                          <p className="label-overline">Total Dockets</p>
                          <p className="text-2xl font-heading font-bold">
                            {revenueReport?.summary?.total_dockets || 0}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="card-industrial">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-green-500/20">
                          <TrendingUp className="h-5 w-5 text-green-500" />
                        </div>
                        <div>
                          <p className="label-overline">Avg Docket Value</p>
                          <p className="text-2xl font-heading font-bold">
                            {formatCurrency(revenueReport?.summary?.avg_docket_value || 0)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {monthlyRevenueData.length > 0 && (
                  <Card className="card-industrial">
                    <CardHeader>
                      <CardTitle className="font-heading text-lg">Monthly Revenue Trend</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={monthlyRevenueData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                            <YAxis 
                              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                              tick={{ fontSize: 12 }}
                            />
                            <Tooltip 
                              formatter={(value, name) => [formatCurrency(value), name === 'revenue' ? 'Revenue' : name]}
                              contentStyle={{ 
                                backgroundColor: 'hsl(var(--card))', 
                                border: '1px solid hsl(var(--border))',
                                borderRadius: '8px'
                              }}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="revenue" 
                              stroke="#ffd108" 
                              strokeWidth={3}
                              dot={{ fill: '#ffd108', strokeWidth: 2 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {customerRevenueData.length > 0 && (
                    <Card className="card-industrial">
                      <CardHeader>
                        <CardTitle className="font-heading text-lg">Revenue by Customer</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="h-[300px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={customerRevenueData} layout="vertical">
                              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                              <XAxis 
                                type="number" 
                                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                              />
                              <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 11 }} />
                              <Tooltip 
                                formatter={(value) => formatCurrency(value)}
                                contentStyle={{ 
                                  backgroundColor: 'hsl(var(--card))', 
                                  border: '1px solid hsl(var(--border))',
                                  borderRadius: '8px'
                                }}
                              />
                              <Bar dataKey="revenue" radius={[0, 4, 4, 0]}>
                                {customerRevenueData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.fill} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {equipmentRevenueData.length > 0 && (
                    <Card className="card-industrial">
                      <CardHeader>
                        <CardTitle className="font-heading text-lg">Revenue by Asset Type</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="h-[300px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={equipmentRevenueData}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={100}
                                paddingAngle={2}
                                dataKey="revenue"
                                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                              >
                                {equipmentRevenueData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.fill} />
                                ))}
                              </Pie>
                              <Tooltip 
                                formatter={(value) => formatCurrency(value)}
                                contentStyle={{ 
                                  backgroundColor: 'hsl(var(--card))', 
                                  border: '1px solid hsl(var(--border))',
                                  borderRadius: '8px'
                                }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Customer Revenue Table */}
                <Card className="card-industrial">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="font-heading text-lg">Customer Revenue Details</CardTitle>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => exportToCSV(revenueReport?.by_customer?.map(c => ({
                        customer_name: c.customer_name,
                        revenue: c.revenue,
                        docket_count: c.docket_count
                      })), 'customer_revenue')}
                      disabled={!revenueReport?.by_customer?.length}
                      data-testid="export-customer-revenue-csv"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export CSV
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {!revenueReport?.by_customer?.length ? (
                      <p className="text-center py-8 text-muted-foreground">No revenue data for this period</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="label-overline">Customer</TableHead>
                              <TableHead className="label-overline text-right">Dockets</TableHead>
                              <TableHead className="label-overline text-right">Revenue</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {revenueReport.by_customer.map((customer) => (
                              <TableRow key={customer.customer_id}>
                                <TableCell className="font-medium">{customer.customer_name}</TableCell>
                                <TableCell className="text-right font-mono">{customer.docket_count}</TableCell>
                                <TableCell className="text-right font-mono font-semibold text-[#ffd108]">
                                  {formatCurrency(customer.revenue)}
                                </TableCell>
                              </TableRow>
                            ))}
                            <TableRow className="bg-muted/50 font-bold">
                              <TableCell>Total</TableCell>
                              <TableCell className="text-right font-mono">
                                {revenueReport.summary.total_dockets}
                              </TableCell>
                              <TableCell className="text-right font-mono text-[#ffd108]">
                                {formatCurrency(revenueReport.summary.total_revenue)}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Jobs Tab */}
              <TabsContent value="jobs" className="space-y-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="card-industrial">
                    <CardContent className="p-4">
                      <p className="label-overline">Total Jobs</p>
                      <p className="text-3xl font-heading font-bold">{jobsReport?.total_jobs || 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="card-industrial">
                    <CardContent className="p-4">
                      <p className="label-overline">Completed</p>
                      <p className="text-3xl font-heading font-bold text-[#ffd108]">
                        {jobsReport?.by_status.completed || 0}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="card-industrial">
                    <CardContent className="p-4">
                      <p className="label-overline">Confirmed</p>
                      <p className="text-3xl font-heading font-bold text-blue-500">
                        {jobsReport?.by_status.confirmed || 0}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="card-industrial">
                    <CardContent className="p-4">
                      <p className="label-overline">Draft</p>
                      <p className="text-3xl font-heading font-bold text-muted-foreground">
                        {jobsReport?.by_status.draft || 0}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {jobStatusData.length > 0 && (
                  <Card className="card-industrial">
                    <CardHeader>
                      <CardTitle className="font-heading text-lg">Jobs by Status</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={jobStatusData}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={100}
                              paddingAngle={2}
                              dataKey="value"
                              label={({ name, value }) => `${name}: ${value}`}
                            >
                              {jobStatusData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Card className="card-industrial">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="font-heading text-lg">Jobs List</CardTitle>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => exportToCSV(jobsReport?.jobs, 'jobs_report')}
                      disabled={!jobsReport?.jobs?.length}
                      data-testid="export-jobs-csv"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export CSV
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {!jobsReport?.jobs?.length ? (
                      <p className="text-center py-8 text-muted-foreground">No jobs in this period</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="label-overline">Date</TableHead>
                              <TableHead className="label-overline">Customer</TableHead>
                              <TableHead className="label-overline">Site</TableHead>
                              <TableHead className="label-overline">Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {jobsReport.jobs.slice(0, 20).map((job) => (
                              <TableRow key={job.id}>
                                <TableCell className="font-mono text-sm">{job.start_date}</TableCell>
                                <TableCell>{job.customer_name}</TableCell>
                                <TableCell className="max-w-[200px] truncate">{job.site_address}</TableCell>
                                <TableCell>
                                  <Badge variant={job.status === 'completed' ? 'default' : 'secondary'}>
                                    {job.status}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        {jobsReport.jobs.length > 20 && (
                          <p className="text-sm text-muted-foreground text-center py-4">
                            Showing 20 of {jobsReport.jobs.length} jobs. Export CSV for full list.
                          </p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Utilization Tab */}
              <TabsContent value="utilization" className="space-y-6">
                {utilizationData.length > 0 && (
                  <Card className="card-industrial">
                    <CardHeader>
                      <CardTitle className="font-heading text-lg">Asset Allocation Days</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={utilizationData} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                            <XAxis type="number" />
                            <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                            <Tooltip 
                              contentStyle={{ 
                                backgroundColor: '#fff', 
                                border: '1px solid #E2E8F0',
                                borderRadius: '4px'
                              }}
                            />
                            <Bar dataKey="days" fill="#ffd108" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Card className="card-industrial">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="font-heading text-lg">Asset Details</CardTitle>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => exportToCSV(utilizationReport?.equipment, 'equipment_utilization')}
                      disabled={!utilizationReport?.equipment?.length}
                      data-testid="export-utilization-csv"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export CSV
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {!utilizationReport?.equipment?.length ? (
                      <p className="text-center py-8 text-muted-foreground">No equipment data</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="label-overline">Asset ID</TableHead>
                              <TableHead className="label-overline">Name</TableHead>
                              <TableHead className="label-overline">Type</TableHead>
                              <TableHead className="label-overline">Allocation Days</TableHead>
                              <TableHead className="label-overline">Job Count</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {utilizationReport.equipment.map((eq) => (
                              <TableRow key={eq.equipment_id}>
                                <TableCell className="font-mono text-sm">{eq.asset_id}</TableCell>
                                <TableCell>{eq.asset_id}</TableCell>
                                <TableCell>{eq.type}</TableCell>
                                <TableCell>
                                  <span className="font-mono font-medium text-[#ffd108]">
                                    {eq.total_allocation_days}
                                  </span>
                                </TableCell>
                                <TableCell className="font-mono">{eq.allocation_count}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* ==================== PREMIUM REPORTS ==================== */}
          <TabsContent value="premium" className="space-y-6">
            {!hasPremiumAccess ? (
              <UpgradePrompt 
                feature="premium_reports"
                title="Premium Reports"
                description="Unlock advanced analytics including Asset Utilization Rates, Downtime Analysis, Job Profitability, Compliance Tracking, Customer Lifetime Value, and Subcontractor Performance reports."
              />
            ) : (
              <>
                {/* Load Premium Reports Button */}
                {!premiumUtilization && (
                  <Card className="card-industrial border-dashed">
                    <CardContent className="py-8 text-center">
                      <Crown className="h-12 w-12 mx-auto mb-4 text-purple-500" />
                      <h3 className="font-heading text-lg font-semibold mb-2">Premium Reports Ready</h3>
                      <p className="text-muted-foreground mb-4">Click Generate to load your premium analytics</p>
                      <Button onClick={fetchPremiumReports} disabled={premiumLoading} className="btn-volt">
                        {premiumLoading ? 'Loading...' : 'Load Premium Reports'}
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {premiumUtilization && (
                  <Tabs defaultValue="utilization-rate">
                    <TabsList className="flex-wrap h-auto gap-1">
                      <TabsTrigger value="utilization-rate" className="text-xs sm:text-sm">
                        <Activity className="h-4 w-4 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline">Utilization</span>
                        <span className="sm:hidden">Util</span>
                      </TabsTrigger>
                      <TabsTrigger value="downtime" className="text-xs sm:text-sm">
                        <Clock className="h-4 w-4 mr-1 sm:mr-2" />
                        Downtime
                      </TabsTrigger>
                      <TabsTrigger value="profitability" className="text-xs sm:text-sm">
                        <TrendingUp className="h-4 w-4 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline">Profitability</span>
                        <span className="sm:hidden">Profit</span>
                      </TabsTrigger>
                      <TabsTrigger value="compliance" className="text-xs sm:text-sm">
                        <ShieldCheck className="h-4 w-4 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline">Compliance</span>
                        <span className="sm:hidden">Comply</span>
                      </TabsTrigger>
                      <TabsTrigger value="customer-ltv" className="text-xs sm:text-sm">
                        <Users className="h-4 w-4 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline">Customer LTV</span>
                        <span className="sm:hidden">LTV</span>
                      </TabsTrigger>
                      <TabsTrigger value="subcontractors" className="text-xs sm:text-sm">
                        <UserCheck className="h-4 w-4 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline">Subcontractors</span>
                        <span className="sm:hidden">Subs</span>
                      </TabsTrigger>
                    </TabsList>

                    {/* Equipment Utilization Rate Tab */}
                    <TabsContent value="utilization-rate" className="space-y-6">
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <Card className="card-industrial">
                          <CardContent className="p-4">
                            <p className="label-overline">Avg Utilization</p>
                            <p className="text-3xl font-heading font-bold text-[#ffd108]">
                              {premiumUtilization?.summary?.avg_utilization_rate || 0}%
                            </p>
                          </CardContent>
                        </Card>
                        <Card className="card-industrial">
                          <CardContent className="p-4">
                            <p className="label-overline">Total Assets</p>
                            <p className="text-3xl font-heading font-bold">
                              {premiumUtilization?.summary?.total_equipment || 0}
                            </p>
                          </CardContent>
                        </Card>
                        <Card className="card-industrial">
                          <CardContent className="p-4">
                            <p className="label-overline">High Utilization (70%+)</p>
                            <p className="text-3xl font-heading font-bold text-green-500">
                              {premiumUtilization?.summary?.high_utilization || 0}
                            </p>
                          </CardContent>
                        </Card>
                        <Card className="card-industrial">
                          <CardContent className="p-4">
                            <p className="label-overline">Low Utilization (&lt;30%)</p>
                            <p className="text-3xl font-heading font-bold text-red-500">
                              {premiumUtilization?.summary?.low_utilization || 0}
                            </p>
                          </CardContent>
                        </Card>
                      </div>

                      {utilizationRateData.length > 0 && (
                        <Card className="card-industrial">
                          <CardHeader>
                            <CardTitle className="font-heading text-lg">Asset Utilization Rates</CardTitle>
                            <CardDescription>Percentage of time equipment was allocated during the period</CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="h-[350px]">
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={utilizationRateData} layout="vertical">
                                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                  <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                                  <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                                  <Tooltip 
                                    formatter={(value) => [`${value}%`, 'Utilization']}
                                    contentStyle={{ 
                                      backgroundColor: 'hsl(var(--card))', 
                                      border: '1px solid hsl(var(--border))',
                                      borderRadius: '8px'
                                    }}
                                  />
                                  <Bar dataKey="rate" radius={[0, 4, 4, 0]}>
                                    {utilizationRateData.map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={entry.fill} />
                                    ))}
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      <Card className="card-industrial">
                        <CardHeader className="flex flex-row items-center justify-between">
                          <CardTitle className="font-heading text-lg">Detailed Utilization</CardTitle>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => exportToCSV(premiumUtilization?.equipment, 'utilization_rates')}
                            data-testid="export-utilization-rate-csv"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Export CSV
                          </Button>
                        </CardHeader>
                        <CardContent>
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="label-overline">Asset</TableHead>
                                  <TableHead className="label-overline">Type</TableHead>
                                  <TableHead className="label-overline text-right">Allocated Days</TableHead>
                                  <TableHead className="label-overline text-right">Idle Days</TableHead>
                                  <TableHead className="label-overline text-right">Utilization</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {premiumUtilization?.equipment?.slice(0, 15).map((eq) => (
                                  <TableRow key={eq.equipment_id}>
                                    <TableCell className="font-medium">{eq.asset_id}</TableCell>
                                    <TableCell>{eq.type}</TableCell>
                                    <TableCell className="text-right font-mono">{eq.allocated_days}</TableCell>
                                    <TableCell className="text-right font-mono text-muted-foreground">{eq.idle_days}</TableCell>
                                    <TableCell className="text-right">
                                      <div className="flex items-center justify-end gap-2">
                                        <Progress 
                                          value={eq.utilization_rate} 
                                          className="w-16 h-2"
                                        />
                                        <span className={`font-mono font-semibold ${
                                          eq.utilization_rate >= 70 ? 'text-green-500' : 
                                          eq.utilization_rate >= 30 ? 'text-yellow-500' : 'text-red-500'
                                        }`}>
                                          {eq.utilization_rate}%
                                        </span>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    {/* Downtime Analysis Tab */}
                    <TabsContent value="downtime" className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Card className="card-industrial">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-lg bg-red-500/20">
                                <Clock className="h-5 w-5 text-red-500" />
                              </div>
                              <div>
                                <p className="label-overline">Total Idle Days</p>
                                <p className="text-2xl font-heading font-bold text-red-500">
                                  {downtimeAnalysis?.summary?.total_idle_days || 0}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        <Card className="card-industrial">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-lg bg-orange-500/20">
                                <AlertTriangle className="h-5 w-5 text-orange-500" />
                              </div>
                              <div>
                                <p className="label-overline">Assets with Downtime</p>
                                <p className="text-2xl font-heading font-bold">
                                  {downtimeAnalysis?.summary?.total_idle_equipment || 0}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      {downtimeStatusData.length > 0 && (
                        <Card className="card-industrial">
                          <CardHeader>
                            <CardTitle className="font-heading text-lg">Downtime by Reason</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="h-[300px]">
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                  <Pie
                                    data={downtimeStatusData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    paddingAngle={2}
                                    dataKey="value"
                                    label={({ name, value }) => `${name}: ${value} days`}
                                  >
                                    {downtimeStatusData.map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                  </Pie>
                                  <Tooltip />
                                </PieChart>
                              </ResponsiveContainer>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      <Card className="card-industrial">
                        <CardHeader className="flex flex-row items-center justify-between">
                          <CardTitle className="font-heading text-lg">Assets with Most Downtime</CardTitle>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => exportToCSV(downtimeAnalysis?.equipment, 'downtime_analysis')}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Export CSV
                          </Button>
                        </CardHeader>
                        <CardContent>
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="label-overline">Asset</TableHead>
                                  <TableHead className="label-overline">Type</TableHead>
                                  <TableHead className="label-overline">Status</TableHead>
                                  <TableHead className="label-overline">Reason</TableHead>
                                  <TableHead className="label-overline text-right">Idle Days</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {downtimeAnalysis?.equipment?.slice(0, 15).map((eq) => (
                                  <TableRow key={eq.equipment_id}>
                                    <TableCell className="font-medium">{eq.asset_id}</TableCell>
                                    <TableCell>{eq.type}</TableCell>
                                    <TableCell>
                                      <Badge variant={eq.status === 'maintenance' ? 'destructive' : 'secondary'}>
                                        {eq.status}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">{eq.reason}</TableCell>
                                    <TableCell className="text-right font-mono font-semibold text-red-500">
                                      {eq.idle_days}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    {/* Job Profitability Tab */}
                    <TabsContent value="profitability" className="space-y-6">
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <Card className="card-industrial">
                          <CardContent className="p-4">
                            <p className="label-overline">Total Revenue</p>
                            <p className="text-2xl font-heading font-bold text-[#ffd108]">
                              {formatCurrency(jobProfitability?.summary?.total_revenue || 0)}
                            </p>
                          </CardContent>
                        </Card>
                        <Card className="card-industrial">
                          <CardContent className="p-4">
                            <p className="label-overline">Total Jobs</p>
                            <p className="text-2xl font-heading font-bold">
                              {jobProfitability?.summary?.total_jobs || 0}
                            </p>
                          </CardContent>
                        </Card>
                        <Card className="card-industrial">
                          <CardContent className="p-4">
                            <p className="label-overline">Avg Revenue/Job</p>
                            <p className="text-2xl font-heading font-bold text-green-500">
                              {formatCurrency(jobProfitability?.summary?.avg_revenue_per_job || 0)}
                            </p>
                          </CardContent>
                        </Card>
                        <Card className="card-industrial">
                          <CardContent className="p-4">
                            <p className="label-overline">Avg Revenue/Day</p>
                            <p className="text-2xl font-heading font-bold text-blue-500">
                              {formatCurrency(jobProfitability?.summary?.avg_revenue_per_day || 0)}
                            </p>
                          </CardContent>
                        </Card>
                      </div>

                      <Card className="card-industrial">
                        <CardHeader className="flex flex-row items-center justify-between">
                          <CardTitle className="font-heading text-lg">Job Profitability</CardTitle>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => exportToCSV(jobProfitability?.jobs, 'job_profitability')}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Export CSV
                          </Button>
                        </CardHeader>
                        <CardContent>
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="label-overline">Customer</TableHead>
                                  <TableHead className="label-overline">Start Date</TableHead>
                                  <TableHead className="label-overline text-right">Days</TableHead>
                                  <TableHead className="label-overline text-right">Dockets</TableHead>
                                  <TableHead className="label-overline text-right">Revenue</TableHead>
                                  <TableHead className="label-overline text-right">$/Day</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {jobProfitability?.jobs?.slice(0, 20).map((job) => (
                                  <TableRow key={job.job_id}>
                                    <TableCell className="font-medium">{job.customer_name}</TableCell>
                                    <TableCell className="font-mono text-sm">{job.start_date}</TableCell>
                                    <TableCell className="text-right font-mono">{job.days}</TableCell>
                                    <TableCell className="text-right font-mono">{job.docket_count}</TableCell>
                                    <TableCell className="text-right font-mono font-semibold text-[#ffd108]">
                                      {formatCurrency(job.total_revenue)}
                                    </TableCell>
                                    <TableCell className="text-right font-mono text-green-500">
                                      {formatCurrency(job.revenue_per_day)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    {/* Compliance Tab */}
                    <TabsContent value="compliance" className="space-y-6">
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <Card className="card-industrial">
                          <CardContent className="p-4">
                            <p className="label-overline">Valid Records</p>
                            <p className="text-3xl font-heading font-bold text-green-500">
                              {complianceSummary?.compliance?.valid || 0}
                            </p>
                          </CardContent>
                        </Card>
                        <Card className="card-industrial">
                          <CardContent className="p-4">
                            <p className="label-overline">Expiring Soon</p>
                            <p className="text-3xl font-heading font-bold text-yellow-500">
                              {complianceSummary?.compliance?.expiring_soon || 0}
                            </p>
                          </CardContent>
                        </Card>
                        <Card className="card-industrial">
                          <CardContent className="p-4">
                            <p className="label-overline">Expired</p>
                            <p className="text-3xl font-heading font-bold text-red-500">
                              {complianceSummary?.compliance?.expired || 0}
                            </p>
                          </CardContent>
                        </Card>
                        <Card className="card-industrial">
                          <CardContent className="p-4">
                            <p className="label-overline">Pre-Start Compliance</p>
                            <p className="text-3xl font-heading font-bold text-[#ffd108]">
                              {complianceSummary?.forms?.compliance_rate || 0}%
                            </p>
                          </CardContent>
                        </Card>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Card className="card-industrial">
                          <CardHeader>
                            <CardTitle className="font-heading text-lg">Form Submissions Trend</CardTitle>
                          </CardHeader>
                          <CardContent>
                            {complianceTrendData.length > 0 ? (
                              <div className="h-[300px]">
                                <ResponsiveContainer width="100%" height="100%">
                                  <AreaChart data={complianceTrendData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                                    <YAxis />
                                    <Tooltip 
                                      contentStyle={{ 
                                        backgroundColor: 'hsl(var(--card))', 
                                        border: '1px solid hsl(var(--border))',
                                        borderRadius: '8px'
                                      }}
                                    />
                                    <Area type="monotone" dataKey="pre_start" stackId="1" stroke="#10B981" fill="#10B981" name="Pre-Start Checks" />
                                    <Area type="monotone" dataKey="incident" stackId="1" stroke="#EF4444" fill="#EF4444" name="Incident Reports" />
                                  </AreaChart>
                                </ResponsiveContainer>
                              </div>
                            ) : (
                              <p className="text-center py-8 text-muted-foreground">No form submission data</p>
                            )}
                          </CardContent>
                        </Card>

                        <Card className="card-industrial">
                          <CardHeader>
                            <CardTitle className="font-heading text-lg">Expiring Compliance Items</CardTitle>
                          </CardHeader>
                          <CardContent>
                            {complianceSummary?.compliance?.expiring_items?.length > 0 ? (
                              <div className="space-y-2">
                                {complianceSummary.compliance.expiring_items.map((item, idx) => (
                                  <div key={idx} className="flex items-center justify-between p-2 bg-yellow-500/10 rounded-lg">
                                    <div>
                                      <p className="font-medium text-sm">{item.type_name}</p>
                                      <p className="text-xs text-muted-foreground">{item.entity_type}</p>
                                    </div>
                                    <Badge variant="outline" className="text-yellow-500 border-yellow-500">
                                      {item.expiry_date}
                                    </Badge>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-center py-8 text-muted-foreground">No items expiring soon</p>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    </TabsContent>

                    {/* Customer LTV Tab */}
                    <TabsContent value="customer-ltv" className="space-y-6">
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <Card className="card-industrial">
                          <CardContent className="p-4">
                            <p className="label-overline">Total Customers</p>
                            <p className="text-3xl font-heading font-bold">
                              {customerLTV?.summary?.total_customers || 0}
                            </p>
                          </CardContent>
                        </Card>
                        <Card className="card-industrial">
                          <CardContent className="p-4">
                            <p className="label-overline">Active Customers</p>
                            <p className="text-3xl font-heading font-bold text-green-500">
                              {customerLTV?.summary?.active_customers || 0}
                            </p>
                          </CardContent>
                        </Card>
                        <Card className="card-industrial">
                          <CardContent className="p-4">
                            <p className="label-overline">Total Lifetime Value</p>
                            <p className="text-2xl font-heading font-bold text-[#ffd108]">
                              {formatCurrency(customerLTV?.summary?.total_lifetime_value || 0)}
                            </p>
                          </CardContent>
                        </Card>
                        <Card className="card-industrial">
                          <CardContent className="p-4">
                            <p className="label-overline">Avg Customer Value</p>
                            <p className="text-2xl font-heading font-bold text-blue-500">
                              {formatCurrency(customerLTV?.summary?.avg_customer_value || 0)}
                            </p>
                          </CardContent>
                        </Card>
                      </div>

                      <Card className="card-industrial">
                        <CardHeader className="flex flex-row items-center justify-between">
                          <CardTitle className="font-heading text-lg">Customer Lifetime Value</CardTitle>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => exportToCSV(customerLTV?.customers, 'customer_ltv')}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Export CSV
                          </Button>
                        </CardHeader>
                        <CardContent>
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="label-overline">Customer</TableHead>
                                  <TableHead className="label-overline text-right">Dockets</TableHead>
                                  <TableHead className="label-overline text-right">Months Active</TableHead>
                                  <TableHead className="label-overline text-right">Monthly Avg</TableHead>
                                  <TableHead className="label-overline text-right">Total Value</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {customerLTV?.customers?.slice(0, 20).map((customer) => (
                                  <TableRow key={customer.customer_id}>
                                    <TableCell className="font-medium">{customer.company_name}</TableCell>
                                    <TableCell className="text-right font-mono">{customer.docket_count}</TableCell>
                                    <TableCell className="text-right font-mono">{customer.months_active}</TableCell>
                                    <TableCell className="text-right font-mono text-blue-500">
                                      {formatCurrency(customer.monthly_avg_revenue)}
                                    </TableCell>
                                    <TableCell className="text-right font-mono font-semibold text-[#ffd108]">
                                      {formatCurrency(customer.total_revenue)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    {/* Subcontractor Performance Tab */}
                    <TabsContent value="subcontractors" className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card className="card-industrial">
                          <CardContent className="p-4">
                            <p className="label-overline">Total Subcontractors</p>
                            <p className="text-3xl font-heading font-bold">
                              {subcontractorPerformance?.summary?.total_subcontractors || 0}
                            </p>
                          </CardContent>
                        </Card>
                        <Card className="card-industrial">
                          <CardContent className="p-4">
                            <p className="label-overline">Active Subcontractors</p>
                            <p className="text-3xl font-heading font-bold text-green-500">
                              {subcontractorPerformance?.summary?.active_subcontractors || 0}
                            </p>
                          </CardContent>
                        </Card>
                        <Card className="card-industrial">
                          <CardContent className="p-4">
                            <p className="label-overline">Total Allocations</p>
                            <p className="text-3xl font-heading font-bold text-[#ffd108]">
                              {subcontractorPerformance?.summary?.total_allocations || 0}
                            </p>
                          </CardContent>
                        </Card>
                      </div>

                      <Card className="card-industrial">
                        <CardHeader className="flex flex-row items-center justify-between">
                          <CardTitle className="font-heading text-lg">Subcontractor Performance</CardTitle>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => exportToCSV(subcontractorPerformance?.subcontractors, 'subcontractor_performance')}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Export CSV
                          </Button>
                        </CardHeader>
                        <CardContent>
                          {!subcontractorPerformance?.subcontractors?.length ? (
                            <p className="text-center py-8 text-muted-foreground">No subcontractor data</p>
                          ) : (
                            <div className="overflow-x-auto">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead className="label-overline">Subcontractor</TableHead>
                                    <TableHead className="label-overline text-right">Allocations</TableHead>
                                    <TableHead className="label-overline text-right">Total Days</TableHead>
                                    <TableHead className="label-overline text-right">Associated Revenue</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {subcontractorPerformance.subcontractors.map((sub) => (
                                    <TableRow key={sub.supplier_id}>
                                      <TableCell className="font-medium">{sub.company_name}</TableCell>
                                      <TableCell className="text-right font-mono">{sub.allocation_count}</TableCell>
                                      <TableCell className="text-right font-mono">{sub.total_days}</TableCell>
                                      <TableCell className="text-right font-mono font-semibold text-[#ffd108]">
                                        {formatCurrency(sub.associated_revenue)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>
                  </Tabs>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </FeatureGate>
  );
};

export default Reports;
